import type {ReactNode} from 'react';
import {Link} from '../app/router';
import {UiStyles} from './UiStyles';
import {cx} from './cx';

/**
 * The screen shell: top bar, body, optional pinned footer.
 *
 * # There is no app chrome, and that is deliberate
 *
 * `App` renders no header, no nav and no footer — every pixel belongs to a
 * screen. So each screen draws its own back link, its own language control and
 * its own attribution line, and this file is the agreement that they all draw
 * them the same. It is layout and nothing else: no data, no routing decisions,
 * no copy. Every string is a prop, because `test/guards.test.ts` asserts there is
 * no Thai in any file outside `src/i18n` and because a primitive that knows a
 * word knows a language.
 *
 * # The footer is pinned by flex, never by `position: fixed`
 *
 * A fixed bar on a mobile browser fights the collapsing URL bar and the
 * on-screen keyboard, and this surface runs on a guest's own phone where both
 * are unavoidable. So the shape is: `Screen` is a column, `ScreenBody` takes the
 * remaining height, `ScreenFooter` is `flex: none` underneath it.
 *
 * ```tsx
 * <Screen>
 *   <TopBar leading={<BackLink to={ROUTES.menu} label={t('common.back')} />}
 *           trailing={<LanguageToggle variant="link" />} />
 *   <ScreenBody scroll>…</ScreenBody>
 *   <ScreenFooter>
 *     <Button variant="primary" block>{t('q4.addToCart')}</Button>
 *   </ScreenFooter>
 * </Screen>
 * ```
 */
export function Screen({
  children,
  className,
  /** `<main>` by default. Pass `'div'` only for a screen nested inside another. */
  as: Tag = 'main',
}: {
  children: ReactNode;
  className?: string;
  as?: 'main' | 'div';
}) {
  return (
    <Tag className={cx('pk-screen', className)}>
      {/* De-duplicated by React; safe from every screen, and what makes a
          screen rendered alone in a test look like the real one. */}
      <UiStyles />
      {children}
    </Tag>
  );
}

/**
 * The bar across the top. Three slots and no opinions about what goes in them.
 *
 * The design puts a back link OR a venue name in `leading` — never both, they
 * occupy the same slot — and the language control in `trailing`, pushed right.
 * The success screen has no `TopBar` at all: the order is committed and there is
 * nowhere to go back to, which is a decision the design makes on purpose.
 */
export function TopBar({
  leading,
  trailing,
  className,
}: {
  leading?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cx('pk-topbar', className)}>
      <div className="pk-topbar-leading">{leading}</div>
      {trailing ? <div className="pk-topbar-trailing">{trailing}</div> : null}
    </header>
  );
}

/**
 * The back affordance, as a real link.
 *
 * A `<Link>` rather than `history.back()`: it renders an `href` a guest can
 * long-press, it survives a cold entry into the middle of the flow (a shared
 * URL, a restored tab) where there is no history to go back to, and the router's
 * own click handling still keeps it a single-page navigation.
 *
 * The glyph is `aria-hidden` and the label carries the meaning, so a
 * screen-reader user hears the word rather than a bracket.
 */
export function BackLink({
  to,
  label,
  replace,
  className,
}: {
  to: string;
  /** Localised. `common.back` unless the screen has a better word for it. */
  label: string;
  replace?: boolean;
  className?: string;
}) {
  return (
    <Link to={to} replace={replace} className={cx('pk-back', 'pokpok-tap', className)}>
      <span className="pk-back-glyph" aria-hidden="true">
        ‹
      </span>
      {label}
    </Link>
  );
}

/**
 * The venue's identity, in the slot the back link would have. Menu screen only.
 *
 * The name is the identity; a `logoUrl` (merchant branding, 2026-08-21) upgrades
 * it to the venue's own mark, with the **name as the image's `alt`** so a screen
 * reader still hears the restaurant rather than "image". A missing, empty or
 * whitespace logo falls back to the name text — the identity is never lost, and a
 * shop with no branding renders exactly as it did before.
 */
export function VenueLabel({
  name,
  logoUrl,
  className,
}: {
  name: string;
  logoUrl?: string | null;
  className?: string;
}) {
  if (typeof logoUrl === 'string' && logoUrl.trim() !== '') {
    return (
      <img
        className={cx('pk-venue', 'pk-venue-logo', className)}
        src={logoUrl.trim()}
        alt={name}
        decoding="async"
      />
    );
  }
  return <p className={cx('pk-venue', className)}>{name}</p>;
}

/**
 * The scrolling middle.
 *
 * `scroll` makes it its own scroll region, and it should be set **only on a
 * screen that has a pinned footer** — that is what pins the footer. Without it
 * the body simply grows and the document scrolls, which is better on every other
 * screen: a document scroll collapses the browser's URL bar and an inner one
 * does not, and that is real vertical space on a small phone.
 *
 * `center` is the short-message layout: the success screen and the two terminal
 * notices.
 */
export function ScreenBody({
  children,
  scroll = false,
  center = false,
  className,
  id,
}: {
  children: ReactNode;
  scroll?: boolean;
  center?: boolean;
  className?: string;
  id?: string;
}) {
  return (
    <div
      id={id}
      className={cx(
        'pk-body',
        scroll ? 'pk-body--scroll' : 'pk-body--grow',
        center && 'pk-body--center',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The pinned bar at the bottom.
 *
 * `bordered` (the default) draws the warm hairline above it, for a screen whose
 * content scrolls underneath. Pass `bordered={false}` where the content is
 * centred or short and there is nothing to separate — the design draws both and
 * means both; a rule under a centred success message reads as a cut-off page.
 */
export function ScreenFooter({
  children,
  bordered = true,
  className,
}: {
  children: ReactNode;
  bordered?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cx('pk-footer', bordered ? 'pk-footer--bordered' : 'pk-footer--plain', className)}
    >
      {children}
    </div>
  );
}

/**
 * A block that sticks to the top of the scroll region — the menu's search and
 * category row.
 *
 * The opaque background is load-bearing, not decoration: without it the list
 * rows show through as they pass under the chips.
 */
export function StickyBlock({children, className}: {children: ReactNode; className?: string}) {
  return <div className={cx('pk-sticky', className)}>{children}</div>;
}
